import getYear from "date-fns/getYear";
import format from "date-fns/format";
import {
  EventsCalendarList,
  EventsCalendar,
  EventsCalendarSearchBar,
} from "components/investorrelations/EventsCalendar";
import NavbarLayout from "components/layouts/NavbarLayout";
import PageHeading from "components/page/PageHeading";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import classNames from "components/ui/classNames";
import Link from "next/link";
import { IAgendaEventType, IIssuerType } from "types/graphqlTypes";
import { CalendarIcon } from "lucide-react";
import { useIntl } from "react-intl";
import useEventsCalendarState from "components/investorrelations/hooks/useEventsCalendarState";
import RightSideFilters, { RightSideFiltersEnum } from "components/investorrelations/RightSideFilters";
import { Provider } from "jotai";

function EventsCalendarPage() {
  const intl = useIntl();
  const filterState = useEventsCalendarState();

  const pageTitle = getPageTitle(filterState.issuerType);
  const seoDescription = getPageDescription(filterState.issuerType);

  return (
    <NavbarLayout>
      <FundamenteiMetadata title={pageTitle} description={seoDescription} />

      <div className="px-4 pb-8 font-aeonik font-normal xl:mx-auto xl:w-full xl:max-w-7xl xl:px-0">
        <PageHeading
          title={intl.formatMessage({
            defaultMessage: "Agenda",
            id: "S7IK4I",
          })}
          description={intl.formatMessage({
            defaultMessage: "Datas dos últimos balanços e todos que já foram atualizados!",
            id: "7rI2x3",
          })}
          icon={<CalendarIcon className="h-6 w-6 fill-transparent" />}
          iconContainerClassName="border-blue-200 bg-blue-50 text-blue-400"
        />

        <div className="mb-4">
          <Filters />
        </div>

        <Provider>
          <EventsCalendar
            {...filterState}
            filterByEventTypes={[
              IAgendaEventType.FirstQuarterEarningsResults,
              IAgendaEventType.SecondQuarterEarningsResults,
              IAgendaEventType.ThirdQuarterEarningsResults,
              IAgendaEventType.FourthQuarterEarningsResults,
            ]}
          >
            {({ error, loading, isLoadingMore, loadMoreEvents }) => {
              return (
                <>
                  <div className="col-span-12 row-start-2 space-y-4 lg:col-span-8 lg:row-start-1">
                    <EventsCalendarSearchBar {...filterState} />

                    <EventsCalendarList
                      {...filterState}
                      loading={loading}
                      isLoadingMore={isLoadingMore}
                      loadMoreEvents={loadMoreEvents}
                      error={error}
                    />
                  </div>

                  <div className="col-span-12 space-y-4 lg:col-span-4">
                    <RightSideFilters
                      {...filterState}
                      enableFilters={[
                        RightSideFiltersEnum.MyPositions,
                        RightSideFiltersEnum.IsInBookmarks,
                        RightSideFiltersEnum.isInSP500,
                        RightSideFiltersEnum.FilterRange,
                        RightSideFiltersEnum.MonthViewCalendar,
                      ]}
                    />
                  </div>
                </>
              );
            }}
          </EventsCalendar>
        </Provider>
      </div>
    </NavbarLayout>
  );
}

export default EventsCalendarPage;

function Filters() {
  const { issuerType, period, myPositions, isInBookmarks, calendarInterval } = useEventsCalendarState();

  const filterByStartDate = format(calendarInterval.startDate, "yyyy-MM-dd");
  const filterByEndDate = format(calendarInterval.endDate, "yyyy-MM-dd");

  return (
    <div className="space-x-4">
      {[
        {
          filterBy: IIssuerType.BrazilianCompany,
          title: "Ações",
          accessibilityDescription: "Filtrar apenas por eventos das Ações",
        },
        {
          filterBy: IIssuerType.AmericanCompany,
          title: "Stocks",
          accessibilityDescription: "Filtrar apenas por eventos das Stocks",
        },
        {
          filterBy: IIssuerType.AmericanReit,
          title: "REITs",
          accessibilityDescription: "Filtrar apenas por eventos dos REITs",
        },
      ].map((node) => {
        const isActive = issuerType === node.filterBy;
        let linkHref = `/events-calendar?issuerType=${node.filterBy.toLocaleLowerCase()}&startDate=${filterByStartDate}&endDate=${filterByEndDate}&period=${period.toLowerCase()}`;

        if (myPositions) {
          linkHref = `${linkHref}&myPositions=1`;
        }

        if (isInBookmarks) {
          linkHref = `${linkHref}&isInBookmarks=1`;
        }

        return (
          <Link
            key={node.filterBy}
            href={linkHref}
            passHref={true}
            aria-label={node.accessibilityDescription}
            className={classNames(
              "font-aeonik text-sm font-medium",
              "border border-gray-200",
              "rounded-full bg-gray-100 px-4 py-2",
              "text-gray-500",
              {
                "hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700": !isActive,
                "border-transparent bg-blue-600 text-white": isActive,
              },
            )}
          >
            {node.title}
          </Link>
        );
      })}
    </div>
  );
}

function getPageTitle(issuerType: IIssuerType | null) {
  const year = getYear(new Date());
  if (issuerType) {
    if (issuerType === IIssuerType.BrazilianCompany) {
      return `Agenda de ${year} para Ações | Fundamentei`;
    }
    if (issuerType === IIssuerType.AmericanCompany) {
      return `Agenda de ${year} para Stocks | Fundamentei`;
    }
    if (issuerType === IIssuerType.AmericanReit) {
      return `Agenda de ${year} para REITs | Fundamentei`;
    }
  }
  return `Agenda de ${year} | Fundamentei`;
}

function getPageDescription(issuerType: IIssuerType | null) {
  if (issuerType) {
    if (issuerType === IIssuerType.BrazilianCompany) {
      return "Agenda completa de balanços e eventos das empresas listadas na B3";
    }
    if (issuerType === IIssuerType.AmericanCompany) {
      return "Agenda completa de balanços e eventos das Stocks listadas na NYSE e Nasdaq";
    }
    if (issuerType === IIssuerType.AmericanReit) {
      return "Agenda completa de balanços e eventos dos REITs listados na NYSE e Nasdaq";
    }
  }
  return "Agenda completa de balanços e eventos importantes das empresas listadas na B3, Nasdaq e NYSE.";
}
